import { useEffect, useState } from "react";
import { format, addDays } from "date-fns";
import { DownloadSimple, Plus, SignIn, SignOut, MapPin } from "@phosphor-icons/react";
import api, { apiError, API_BASE } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { zar, fmtDate, fmtTime, fmtDayShort } from "../lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const LEAVE_STATUS = {
  pending: "bg-[#D99A29]/15 text-[#8a6414]",
  approved: "bg-[#A9C97E]/10 text-[#A9C97E]",
  declined: "bg-[#F87171]/10 text-[#F87171]",
};

function ClockCard({ hours, onDone }) {
  const [busy, setBusy] = useState(false);
  const [, setTick] = useState(0);
  const open = hours.find((h) => !h.clock_out);

  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, [open]);

  const getPosition = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => resolve(null),
        { timeout: 8000, maximumAge: 60000 }
      );
    });

  const clock = async (action) => {
    setBusy(true);
    try {
      const pos = await getPosition();
      const { data } = await api.post("/clock", { action, ...(pos || {}) });
      toast.success(
        action === "in"
          ? `Clocked in at ${data.time}${data.gps_captured ? " · location captured" : " · no location (permission denied)"}`
          : `Clocked out at ${data.time} — ${data.hours}h today${data.gps_captured ? " · location captured" : ""}`
      );
      onDone();
    } catch (e) {
      toast.error(apiError(e, "Clock failed"));
    } finally {
      setBusy(false);
    }
  };

  const elapsed = open ? Math.max(0, (Date.now() - new Date(open.clock_in).getTime()) / 60000) : 0;

  return (
    <Card className="border-border shadow-none" data-testid="my-clock-card">
      <CardHeader><CardTitle className="font-heading text-lg">Clock in / out</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {open ? (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Clocked in at {fmtTime(open.clock_in)}</p>
              <p className="font-heading text-2xl font-bold" data-testid="my-clock-elapsed">
                {Math.floor(elapsed / 60)}h {String(Math.floor(elapsed % 60)).padStart(2, "0")}m
              </p>
            </div>
            <Button onClick={() => clock("out")} disabled={busy} data-testid="my-clock-out-btn"
              className="bg-[#A78BFA] hover:bg-[#8B5CF6] text-white h-12 px-6">
              <SignOut size={18} className="mr-2" /> {busy ? "Working…" : "Clock out"}
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <MapPin size={16} /> Not clocked in — your phone's location is attached (informational only)
            </p>
            <Button onClick={() => clock("in")} disabled={busy} data-testid="my-clock-in-btn"
              className="bg-[#4A5D23] hover:bg-[#3d4d1d] text-white h-12 px-6">
              <SignIn size={18} className="mr-2" /> {busy ? "Working…" : "Clock in"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function MyPortal() {
  const { user } = useAuth();
  const isManager = user?.role === "manager";
  const [profile, setProfile] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [balance, setBalance] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [myLeave, setMyLeave] = useState([]);
  const [hours, setHours] = useState([]);
  const [showRequest, setShowRequest] = useState(false);
  const [req, setReq] = useState({ type: "annual", start_date: format(addDays(new Date(), 7), "yyyy-MM-dd"), end_date: format(addDays(new Date(), 7), "yyyy-MM-dd"), reason: "" });

  const load = () => {
    api.get("/my/profile").then((r) => setProfile(r.data)).catch(() => {});
    api.get("/my/shifts").then((r) => setShifts(r.data)).catch(() => {});
    api.get("/my/leave/balance").then((r) => setBalance(r.data)).catch(() => {});
    api.get("/my/payslips").then((r) => setPayslips(r.data)).catch(() => {});
    api.get("/leave").then((r) => setMyLeave(r.data)).catch(() => {});
    api.get("/my/hours").then((r) => setHours(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const submitLeave = async () => {
    try {
      await api.post("/leave", req);
      toast.success("Leave request submitted — managers notified");
      setShowRequest(false);
      load();
    } catch (e) {
      toast.error(apiError(e, "Submit failed"));
    }
  };

  const upcoming = shifts.filter((s) => s.date >= format(new Date(), "yyyy-MM-dd"));
  const weekHours = hours.slice(0, 10);

  return (
    <div className="space-y-6" data-testid="my-portal">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Hi {profile?.first_name}</h1>
          <p className="text-muted-foreground mt-1">{profile?.position}</p>
        </div>
        <Button data-testid="request-leave-btn" onClick={() => setShowRequest(true)} className="bg-primary hover:bg-[#8B5CF6] text-white">
          <Plus size={16} className="mr-1" /> Request leave
        </Button>
      </div>

      <div>
        <ClockCard hours={hours} onDone={load} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border-border shadow-none" data-testid="my-schedule">
          <CardHeader><CardTitle className="font-heading text-lg">My upcoming shifts</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {upcoming.length === 0 && <p className="text-sm text-muted-foreground">No published shifts coming up.</p>}
            {upcoming.map((s) => (
              <div key={s.id} className="flex justify-between border-b last:border-0 pb-2" data-testid={`my-shift-${s.id}`}>
                <span className="text-sm font-medium">{fmtDayShort(s.date)}</span>
                <span className="text-sm num">{s.start}–{s.end} · {s.section}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border-border shadow-none" data-testid="my-leave">
          <CardHeader><CardTitle className="font-heading text-lg">Leave balance</CardTitle></CardHeader>
          <CardContent>
            {balance && (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Accrued</span><span className="num font-medium" data-testid="my-accrued">{balance.accrued} days</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Taken</span><span className="num font-medium">{balance.taken} days</span></div>
                <div className="flex justify-between border-t pt-2"><span className="font-medium">Balance</span><span className="num font-bold text-lg" data-testid="my-balance">{balance.balance} days</span></div>
              </div>
            )}
            <div className="mt-4 space-y-2">
              {myLeave.slice(0, 5).map((l) => (
                <div key={l.id} className="flex items-center justify-between text-sm" data-testid={`my-leave-${l.id}`}>
                  <span className="capitalize">{l.type} · {fmtDate(l.start_date)}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${LEAVE_STATUS[l.status]}`}>
                    {l.status}{l.status === "approved" && !l.paid ? " (unpaid)" : ""}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {!isManager && (
        <Card className="border-border shadow-none" data-testid="my-payslips">
          <CardHeader><CardTitle className="font-heading text-lg">My payslips</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {payslips.length === 0 && <p className="text-sm text-muted-foreground">No payslips yet.</p>}
            {payslips.map((p) => (
              <div key={p.run_id} className="flex items-center justify-between border-b last:border-0 pb-2" data-testid={`my-payslip-${p.run_id}`}>
                <div>
                  <p className="text-sm font-medium">{fmtDate(p.period_start)} → {fmtDate(p.period_end)}</p>
                  <p className="text-xs text-muted-foreground">Net {zar(p.line.net)}</p>
                </div>
                <a href={`${API_BASE}/payroll/runs/${p.run_id}/payslip/${p.line.employee_id}`} data-testid={`my-payslip-dl-${p.run_id}`}>
                  <Button variant="outline" size="sm"><DownloadSimple size={14} className="mr-1" /> PDF</Button>
                </a>
              </div>
            ))}
          </CardContent>
        </Card>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-border shadow-none" data-testid="my-hours">
          <CardHeader><CardTitle className="font-heading text-lg">Recent clock-ins</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {weekHours.length === 0 && <p className="text-sm text-muted-foreground">No clock entries yet.</p>}
            {weekHours.map((h) => (
              <div key={h.id} className="flex justify-between text-sm border-b last:border-0 pb-2" data-testid={`my-hours-${h.id}`}>
                <span>{h.date}</span>
                <span className="num">{fmtTime(h.clock_in)} → {h.clock_out ? fmtTime(h.clock_out) : "open"} · {h.hours}h</span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border-border shadow-none" data-testid="my-profile">
          <CardHeader><CardTitle className="font-heading text-lg">My details</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-2">
            {profile && (
              <>
                <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span>{profile.email}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Phone</span><span>{profile.phone || "—"}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Start date</span><span>{fmtDate(profile.start_date)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Salary</span><span className="num">{profile.salary_type === "hourly" ? `${zar(profile.rate)}/h` : `${zar(profile.rate)}/mo`}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">ID / passport</span><span>{profile.id_number || "—"}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Bank</span><span>{profile.bank_name ? `${profile.bank_name} ···${(profile.account_number || "").slice(-4)}` : "—"}</span></div>
                <p className="text-xs text-muted-foreground pt-2">Something wrong? Ask your manager to update your record.</p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={showRequest} onOpenChange={setShowRequest}>
        <DialogContent className="max-w-sm" data-testid="leave-request-dialog">
          <DialogHeader><DialogTitle className="font-heading">Request leave</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={req.type} onValueChange={(v) => setReq({ ...req, type: v })}>
                <SelectTrigger data-testid="leave-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="annual">Annual</SelectItem>
                  <SelectItem value="sick">Sick</SelectItem>
                  <SelectItem value="family">Family responsibility</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>From</Label><Input type="date" data-testid="leave-start" value={req.start_date} onChange={(e) => setReq({ ...req, start_date: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>To</Label><Input type="date" data-testid="leave-end" value={req.end_date} onChange={(e) => setReq({ ...req, end_date: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Reason (optional)</Label><Textarea data-testid="leave-reason" value={req.reason} onChange={(e) => setReq({ ...req, reason: e.target.value })} /></div>
            <Button onClick={submitLeave} data-testid="leave-submit" className="w-full bg-primary hover:bg-[#8B5CF6] text-white">Submit request</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
